// ── Voice Indicators ──
// HUD overlay showing who is speaking in voice chat.
// Displays local mic status (muted/speaking) and a list of speaking teammates.
// Follows the same styling as existing HUD elements (military theme).

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

interface VoiceChatLike {
  isMuted(): boolean;
  isTransmitting(): boolean;
  getPeers(): ReadonlyMap<number, { speaking: boolean; name?: string }>;
  getState(): string;
  onChange(fn: () => void): void;
  offChange(fn: () => void): void;
  // F-AV-005: optional VAD subscription so the HUD can show live mic level
  // separately from PTT-down state.
  onVoiceActivity?(fn: (speaking: boolean) => void): void;
  offVoiceActivity?(fn: (speaking: boolean) => void): void;
  isLocalSpeaking?(): boolean;
  // F-AV-006: optional permission watcher so the HUD can surface a "denied" banner.
  onPermissionChange?(fn: (state: MicPermissionState) => void): void;
  offPermissionChange?(fn: (state: MicPermissionState) => void): void;
  getMicPermissionState?(): Promise<MicPermissionState>;
  requestMicPermission?(): Promise<boolean> | boolean;
}

export interface VoiceSpeakerState {
  playerId: number | string;
  name: string;
  speaking: boolean;
}

interface SpeakingEntry {
  id: string;
  name: string;
}

function truncateSpeakerName(name: string): string {
  return name.length > 12 ? `${name.slice(0, 9)}...` : name;
}

export class VoiceIndicators {
  private overlay: HTMLElement;
  private voiceChat: VoiceChatLike;
  private boundUpdate: () => void;
  private boundVAD: ((speaking: boolean) => void) | null = null;
  private boundPermission: ((state: MicPermissionState) => void) | null = null;
  private speakingNames: string[] = [];
  private speakingEntries: SpeakingEntry[] = [];
  private talkStartedAt = new Map<string, number>();
  private talkAccumulatedMs = new Map<string, number>();
  private meterTimer: ReturnType<typeof setInterval> | null = null;
  private boundRoundStart = () => this.resetRoundTalkTimes();
  private vadActive = false;
  private permissionState: MicPermissionState = 'unknown';
  private mutedCallback: ((muted: boolean) => void) | null = null;
  private disposed = false;

  constructor(voiceChat: VoiceChatLike) {
    this.voiceChat = voiceChat;

    this.overlay = document.createElement('div');
    this.overlay.style.cssText =
      'position: fixed; bottom: calc(120px + env(safe-area-inset-bottom, 0px)); left: 16px; z-index: 50; ' +
      'font-family: "Courier New", monospace; color: #c8c090; ' +
      'pointer-events: none; transition: opacity 0.2s;';

    this.boundUpdate = () => this.update();
    this.voiceChat.onChange(this.boundUpdate);

    // F-AV-005 — show a live mic-level indicator when VAD trips (separate from PTT).
    if (this.voiceChat.onVoiceActivity) {
      this.boundVAD = (speaking) => {
        this.vadActive = speaking;
        this.update();
      };
      this.voiceChat.onVoiceActivity(this.boundVAD);
    }

    // F-AV-006 — surface mic-permission denial.
    if (this.voiceChat.onPermissionChange) {
      this.boundPermission = (state) => {
        this.permissionState = state;
        this.update();
      };
      this.voiceChat.onPermissionChange(this.boundPermission);
      // Seed initial state so banner appears even when nothing has changed yet.
      this.voiceChat.getMicPermissionState?.().then((s) => {
        if (this.disposed) return;
        this.permissionState = s;
        this.update();
      }).catch(() => { /* permission API unavailable — leave 'unknown' */ });
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('round_start', this.boundRoundStart);
    }
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('round_start', this.boundRoundStart);
    }
  }

  /** Get the overlay DOM element. */
  getOverlay(): HTMLElement {
    return this.overlay;
  }

  /** Append overlay to document.body. */
  mount(): void {
    document.body.appendChild(this.overlay);
  }

  /** Register a local mute toggle callback. Pass null to remove the button. */
  setMutedCallback(cb: ((muted: boolean) => void) | null): void {
    this.mutedCallback = cb;
    this.render();
  }

  /** Refresh the speaking list from server/voice state and redraw the UI. */
  updateSpeakers(speakers: VoiceSpeakerState[]): void {
    if (this.disposed) return;

    const now = Date.now();
    const activeIds = new Set<string>();
    this.speakingEntries = [];

    for (const speaker of speakers) {
      const id = String(speaker.playerId);
      const name = speaker.name || `Player ${speaker.playerId}`;
      if (speaker.speaking) {
        activeIds.add(id);
        if (!this.talkStartedAt.has(id)) this.talkStartedAt.set(id, now);
        this.speakingEntries.push({ id, name });
      } else {
        this.finalizeTalkSegment(id, now);
      }
    }

    for (const id of Array.from(this.talkStartedAt.keys())) {
      if (!activeIds.has(id)) this.finalizeTalkSegment(id, now);
    }

    this.speakingNames = this.speakingEntries.map((speaker) => speaker.name);
    this.updateMeterTimer();
    this.render();
  }

  /** Refresh the UI from current voiceChat state. */
  update(): void {
    if (this.disposed) return;

    const peers = this.voiceChat.getPeers();
    // Collect speaking peer names.
    // F-UI-009: Walk entries once instead of materialising peers.entries() and
    // scanning it once per speaking peer (was O(n²) per voiceChange tick).
    const speakers: VoiceSpeakerState[] = [];
    for (const [id, peer] of peers) {
      speakers.push({
        playerId: id,
        name: peer.name ?? `Player ${id}`,
        speaking: peer.speaking,
      });
    }

    this.updateSpeakers(speakers);
  }

  private render(): void {
    if (this.disposed) return;

    const muted = this.voiceChat.isMuted();
    const transmitting = this.voiceChat.isTransmitting();
    const hasSpeakers = this.speakingNames.length > 0;
    const denied = this.permissionState === 'denied';
    const visible = muted || transmitting || hasSpeakers || denied || this.mutedCallback !== null;

    // Visibility
    if (visible) {
      this.overlay.classList.remove('voice-hidden');
    } else {
      this.overlay.classList.add('voice-hidden');
    }

    // Muted state
    if (muted) {
      this.overlay.classList.add('voice-muted');
    } else {
      this.overlay.classList.remove('voice-muted');
    }

    // Speaking state
    if (transmitting) {
      this.overlay.classList.add('voice-speaking');
    } else {
      this.overlay.classList.remove('voice-speaking');
    }

    // Build inner HTML
    let html = '';

    if (this.mutedCallback) {
      const buttonColor = muted ? '#e94560' : 'rgba(20,24,20,0.9)';
      const iconColor = muted ? '#ffffff' : '#c8c090';
      const label = muted ? 'Unmute microphone' : 'Mute microphone';
      html += '<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">' +
        `<button type="button" data-role="mic-toggle" aria-label="${label}" title="${label}" ` +
        'style="pointer-events:auto;width:24px;height:24px;padding:0;display:flex;align-items:center;justify-content:center;' +
        `background:${buttonColor};border:1px solid rgba(255,255,255,0.22);border-radius:3px;cursor:pointer;">` +
        `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false"><path fill="${iconColor}" d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Zm5 8a1 1 0 1 0-2 0 3 3 0 1 1-6 0 1 1 0 1 0-2 0 5 5 0 0 0 4 4.9V19H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-3.1a5 5 0 0 0 4-4.9Z"/></svg>` +
        '</button>' +
        '<span style="font-size:10px;color:#8aa078;letter-spacing:1px;">MIC</span>' +
        '</div>';
    }

    // F-AV-006: surface mic-permission denial. Renders above the rest so the
    // user sees instructions before any speak/mute UI.
    if (denied) {
      html += '<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;' +
        ' padding: 4px 8px; background: rgba(60,12,12,0.85); border: 1px solid #e94560; border-radius: 3px;">' +
        '<span style="color: #e94560; font-size: 14px;">&#x26A0;</span>' +
        '<span style="font-size: 11px; color: #e94560; letter-spacing: 1px;">MIC ACCESS DENIED</span>' +
        '<button type="button" data-role="mic-retry" style="pointer-events:auto;background:rgba(233,69,96,0.18);border:1px solid rgba(233,69,96,0.7);color:#ffd4dc;font-size:10px;letter-spacing:1px;padding:2px 6px;border-radius:3px;cursor:pointer;">Retry</button>' +
        '</div>';
    }

    // Local mic status
    if (muted) {
      html += '<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">' +
        '<span style="color: #e94560; font-size: 14px;">&#x1F507;</span>' +
        '<span style="font-size: 11px; color: #e94560; letter-spacing: 1px;">MUTED</span></div>';
    } else if (transmitting) {
      // F-AV-005: PTT-held + VAD active = LIVE; PTT-held + no VAD = TRANSMITTING (silent).
      const liveLabel = this.vadActive ? 'LIVE' : 'TRANSMITTING';
      const liveColor = this.vadActive ? '#4ade80' : '#a8c060';
      html += '<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">' +
        `<span style="color: ${liveColor}; font-size: 14px;">&#x1F3A4;</span>` +
        `<span style="font-size: 11px; color: ${liveColor}; letter-spacing: 1px;">${liveLabel}</span></div>`;
    }

    // Speaking teammates (escape names — injected via innerHTML)
    for (const speaker of this.speakingEntries) {
      const displayName = truncateSpeakerName(speaker.name);
      const talkTime = this.formatTalkTime(speaker.id);
      html += '<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px; ' +
        'padding: 2px 8px; background: rgba(20,24,20,0.8); border-radius: 3px;">' +
        '<span style="color: #4ade80; font-size: 10px;">&#x1F3A4;</span>' +
        '<span title="' + escapeHtml(speaker.name) + '" style="font-size: 11px; color: #c8c090; max-width: 12ch; ' +
        'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">' +
        escapeHtml(displayName) + '</span>' +
        '<span style="font-size: 10px; color: #8aa078; opacity: 0.9; min-width: 4ch; text-align: right;">' +
        escapeHtml(talkTime) + '</span></div>';
    }

    this.overlay.innerHTML = html;
    const muteButton = this.overlay.querySelector<HTMLButtonElement>('[data-role="mic-toggle"]');
    muteButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.mutedCallback?.(!this.voiceChat.isMuted());
    });
    const retryButton = this.overlay.querySelector<HTMLButtonElement>('[data-role="mic-retry"]');
    retryButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.retryMicPermission();
    });
  }

  private finalizeTalkSegment(id: string, now = Date.now()): void {
    const started = this.talkStartedAt.get(id);
    if (started === undefined) return;
    this.talkAccumulatedMs.set(id, (this.talkAccumulatedMs.get(id) ?? 0) + Math.max(0, now - started));
    this.talkStartedAt.delete(id);
  }

  private getTalkMs(id: string): number {
    const started = this.talkStartedAt.get(id);
    const activeMs = started === undefined ? 0 : Math.max(0, Date.now() - started);
    return (this.talkAccumulatedMs.get(id) ?? 0) + activeMs;
  }

  private formatTalkTime(id: string): string {
    const totalSeconds = Math.floor(this.getTalkMs(id) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private updateMeterTimer(): void {
    if (this.speakingEntries.length > 0) {
      if (this.meterTimer !== null) return;
      this.meterTimer = setInterval(() => {
        if (this.disposed || this.speakingEntries.length === 0) {
          this.updateMeterTimer();
          return;
        }
        this.render();
      }, 1000);
      return;
    }

    if (this.meterTimer !== null) {
      clearInterval(this.meterTimer);
      this.meterTimer = null;
    }
  }

  resetRoundTalkTimes(): void {
    const now = Date.now();
    this.talkStartedAt.clear();
    this.talkAccumulatedMs.clear();
    for (const speaker of this.speakingEntries) {
      this.talkStartedAt.set(speaker.id, now);
    }
    this.updateMeterTimer();
    this.render();
  }

  private retryMicPermission(): void {
    if (!this.voiceChat.requestMicPermission) {
      location.reload();
      return;
    }
    void Promise.resolve(this.voiceChat.requestMicPermission()).then((granted) => {
      this.permissionState = granted ? 'granted' : 'denied';
      this.update();
    }).catch(() => {
      this.permissionState = 'denied';
      this.update();
    });
  }

  /** Get the list of currently speaking peer names. */
  getSpeakingNames(): string[] {
    return [...this.speakingNames];
  }

  /** Clean up DOM and listeners. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.voiceChat.offChange(this.boundUpdate);
    if (this.boundVAD) this.voiceChat.offVoiceActivity?.(this.boundVAD);
    if (this.boundPermission) this.voiceChat.offPermissionChange?.(this.boundPermission);
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('round_start', this.boundRoundStart);
    }
    if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      document.removeEventListener('round_start', this.boundRoundStart);
    }
    if (this.meterTimer !== null) clearInterval(this.meterTimer);
    this.boundVAD = null;
    this.boundPermission = null;
    this.meterTimer = null;
    this.overlay.remove();
  }
}
